/** Max inclusive span for a custom analytics export window. */
export const ANALYTICS_MAX_RANGE_DAYS = 366;

/** Calendar day key in Asia/Jerusalem (YYYY-MM-DD). */
export function jerusalemDayKey(date: Date) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Jerusalem',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

/** Inclusive day count between two YYYY-MM-DD keys (UTC date math). */
export function inclusiveDayCount(startDay: string, endDay: string) {
    const [sy, sm, sd] = startDay.split('-').map(Number);
    const [ey, em, ed] = endDay.split('-').map(Number);
    if (!sy || !sm || !sd || !ey || !em || !ed) return 0;
    const start = Date.UTC(sy, sm - 1, sd);
    const end = Date.UTC(ey, em - 1, ed);
    if (end < start) return 0;
    return Math.floor((end - start) / 86_400_000) + 1;
}

/** Shift a YYYY-MM-DD key by `delta` calendar days. */
export function shiftDayKey(dayKey: string, delta: number) {
    const [year, month, day] = dayKey.split('-').map(Number);
    return new Date(Date.UTC(year!, month! - 1, day! + delta))
        .toISOString()
        .slice(0, 10);
}

/** Last `days` inclusive ending today (Jerusalem). */
export function rollingRangeDays(days: number) {
    const end = jerusalemDayKey(new Date());
    const start = shiftDayKey(end, -(days - 1));
    return { start, end };
}

/** First day of this month through today (Jerusalem). */
export function monthToDateRange() {
    const end = jerusalemDayKey(new Date());
    return { start: `${end.slice(0, 7)}-01`, end };
}

/** Previous calendar month (Jerusalem). */
export function lastMonthRange() {
    const today = jerusalemDayKey(new Date());
    const thisMonthStart = `${today.slice(0, 7)}-01`;
    const end = shiftDayKey(thisMonthStart, -1);
    return { start: `${end.slice(0, 7)}-01`, end };
}

/** January 1 of this year through today (Jerusalem). */
export function yearToDateRange() {
    const end = jerusalemDayKey(new Date());
    return { start: `${end.slice(0, 4)}-01-01`, end };
}

/** Fill a contiguous daily series ending today (Jerusalem), zero-filled. */
export function fillDailySeries(
    days: number,
    rows: { day: string; value: number }[],
): { day: string; count: number }[] {
    const { start, end } = rollingRangeDays(days);
    return fillDailySeriesBetween(start, end, rows);
}

/** Fill a contiguous daily series between inclusive YYYY-MM-DD bounds. */
export function fillDailySeriesBetween(
    startDay: string,
    endDay: string,
    rows: { day: string; value: number }[],
): { day: string; count: number }[] {
    const byDay = new Map(
        rows.map((row) => [row.day, Number(row.value)] as const),
    );
    const total = inclusiveDayCount(startDay, endDay);
    const series: { day: string; count: number }[] = [];
    for (let i = 0; i < total; i++) {
        const key = shiftDayKey(startDay, i);
        series.push({ day: key, count: byDay.get(key) ?? 0 });
    }
    return series;
}

/** Calendar month key in Asia/Jerusalem (YYYY-MM). */
export function jerusalemMonthKey(date: Date) {
    return jerusalemDayKey(date).slice(0, 7);
}

/** Shift a YYYY-MM key by `delta` calendar months. */
export function shiftMonthKey(monthKey: string, delta: number) {
    const [year, month] = monthKey.split('-').map(Number);
    const shifted = new Date(Date.UTC(year!, month! - 1 + delta, 1));
    return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Last `months` inclusive ending this month (Jerusalem). */
export function rollingRangeMonths(months: number) {
    const end = jerusalemMonthKey(new Date());
    const start = shiftMonthKey(end, -(months - 1));
    return { start, end };
}

/** Fill a contiguous monthly series ending this month (Jerusalem), zero-filled. */
export function fillMonthlySeries(
    months: number,
    rows: { month: string; value: number }[],
): { day: string; count: number }[] {
    const byMonth = new Map(
        rows.map((row) => [row.month, Number(row.value)] as const),
    );
    const { start } = rollingRangeMonths(months);
    const series: { day: string; count: number }[] = [];
    for (let i = 0; i < months; i++) {
        const key = shiftMonthKey(start, i);
        series.push({ day: key, count: byMonth.get(key) ?? 0 });
    }
    return series;
}
